import { Link } from "react-router-dom";
import { FileDown, X } from "lucide-react";
import { API, STAGE_META, STATUS_FLOW, shortDate, stageOf } from "./shared";

// 4-step production indicator: Brief received / In production / Quality check / Ready.
export function StatusStepper({ status, testId }) {
  const stage = stageOf(status);
  return (
    <div className="flex items-start gap-1.5" data-testid={testId || "project-stepper"}>
      {STATUS_FLOW.map((s, si) => {
        const done = si < stage;
        const active = si === stage;
        return (
          <div key={s} className="min-w-0 flex-1">
            <div
              className={`h-1 rounded-full ${
                done ? "bg-emerald-400/70" : active ? "bg-gradient-to-r from-sky-400 to-amber-400" : "bg-white/10"
              }`}
            />
            <p
              className={`mt-1.5 truncate text-[9px] font-semibold uppercase tracking-wider ${
                done ? "text-emerald-400" : active ? "text-amber-300" : "text-slate-600"
              }`}
            >
              {done ? "✓ " : ""}{STAGE_META[s].step}
            </p>
          </div>
        );
      })}
    </div>
  );
}

export function StatusChip({ status }) {
  return (
    <span
      data-testid="order-status-chip"
      className={`shrink-0 rounded-full border px-3 py-1 text-xs font-semibold ${STAGE_META[status]?.chip || STAGE_META.Submitted.chip}`}
    >
      {STAGE_META[status]?.step || status}
    </span>
  );
}

// Compact project row used across Overview, Projects and Deliveries.
export function ProjectRow({ brief, detail = true, download = false, dateIso, dateLabel = "Created" }) {
  const name = brief.request.length > 64 ? `${brief.request.slice(0, 64)}...` : brief.request;
  return (
    <div
      data-testid={`project-row-${brief.brief_id}`}
      className="rounded-2xl border border-white/10 bg-[#0C1222]/80 p-4 transition-colors hover:border-white/20"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="min-w-0 flex-1 truncate text-sm font-bold text-white">{name}</p>
        <StatusChip status={brief.status} />
      </div>
      <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-slate-500">
        {dateLabel} {shortDate(dateIso || brief.created_at)}
      </p>
      <div className="mt-3">
        <StatusStepper status={brief.status} />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {download && brief.status === "Delivered" && brief.delivered_path && (
          <a
            data-testid="order-download-button"
            href={`${API}/files/${brief.delivered_path}`}
            className="inline-flex items-center gap-2 rounded-full bg-emerald-400 px-4 py-2 text-xs font-extrabold uppercase tracking-wide text-[#05140d] transition-colors hover:bg-emerald-300"
          >
            <FileDown className="h-3.5 w-3.5" />
            Download PNG
          </a>
        )}
        {detail && (
          <Link
            to={`/dashboard/projects/${brief.brief_id}`}
            data-testid={`view-project-${brief.brief_id}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-400/10 px-4 py-2 text-xs font-bold text-amber-300 transition-colors hover:bg-amber-400/20"
          >
            View Project
          </Link>
        )}
      </div>
    </div>
  );
}

export function EmptyState({ testId, eyebrow, text, children }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-dashed border-white/10 p-10 text-center">
      <p className="font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-amber-300">{eyebrow}</p>
      <p className="mt-2 text-sm leading-relaxed text-slate-400">{text}</p>
      {children && <div className="mt-5">{children}</div>}
    </div>
  );
}

export function Modal({ testId, closeTestId, title, titleColor = "text-amber-300", onClose, children }) {
  return (
    <div
      data-testid={testId}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="glass w-full max-w-sm rounded-3xl border border-white/10 bg-[#0C1222] p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <p className={`font-mono text-[10px] font-bold uppercase tracking-[0.25em] ${titleColor}`}>{title}</p>
          <button data-testid={closeTestId} onClick={onClose} className="text-slate-500 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}
